import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Card, CardActionArea, CardContent,
  Stepper, Step, StepLabel, Button, TextField, Alert, Chip
} from '@mui/material';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import GavelIcon from '@mui/icons-material/Gavel';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import ArticleIcon from '@mui/icons-material/Article';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';

// Each template defines its icon/colour, a short intro, the ordered steps
// (each step collects one field) and a builder that turns the collected
// answers into a deep-link into an EXISTING search page. No new search
// logic — the wizard only assembles a ?q= Lucene query + params.
const TEMPLATES = [
  {
    key: 'speeches-by-mp',
    label: 'Speeches by a parliamentarian',
    icon: <RecordVoiceOverIcon />,
    color: '#1a73e8',
    description: "Find Hansard speeches made by a specific member or senator, optionally narrowed to a year.",
    steps: [
      {
        field: 'name',
        label: 'Parliamentarian name',
        required: true,
        placeholder: 'e.g. Julia Gillard',
        help: "Enter the speaker's name as it appears in Hansard (surname is usually enough, e.g. \"Gillard\").",
      },
      {
        field: 'year',
        label: 'Year (optional)',
        required: false,
        placeholder: 'e.g. 2012',
        help: 'Optionally restrict the results to a single calendar year. Leave blank for all years.',
      },
    ],
    build: (a) => {
      const params = new URLSearchParams();
      params.set('mode', 'advanced');
      params.set('q', `speaker_name:"${a.name.trim()}"`);
      const yr = (a.year || '').trim();
      if (/^\d{4}$/.test(yr)) params.set('date_year', yr);
      return `/hansard?${params.toString()}`;
    },
  },
  {
    key: 'second-reading',
    label: 'Second reading speeches for a bill',
    icon: <GavelIcon />,
    color: '#7b1fa2',
    description: 'Find the second reading debate speeches relating to a particular bill.',
    steps: [
      {
        field: 'bill',
        label: 'Bill name',
        required: true,
        placeholder: 'e.g. Clean Energy Bill',
        help: 'Enter the bill title (or a distinctive part of it). We will look for its second reading debate in Hansard.',
      },
    ],
    build: (a) => {
      const q = `debate_title:"${a.bill.trim()}" AND "second reading"`;
      return `/hansard?q=${encodeURIComponent(q)}`;
    },
  },
  {
    key: 'division-votes',
    label: "A parliamentarian's division votes",
    icon: <HowToVoteIcon />,
    color: '#2e7d32',
    description: 'See divisions (recorded votes) involving a particular parliamentarian.',
    steps: [
      {
        field: 'name',
        label: 'Parliamentarian name',
        required: true,
        placeholder: 'e.g. Anthony Albanese',
        help: 'Enter the name of the member or senator whose division votes you want to look at.',
      },
    ],
    build: (a) => `/divisions?q=${encodeURIComponent(`"${a.name.trim()}"`)}`,
  },
  {
    key: 'bill-by-topic',
    label: 'Find a bill by topic',
    icon: <ArticleIcon />,
    color: '#c62828',
    description: 'Find bills that mention a particular topic or keyword.',
    steps: [
      {
        field: 'topic',
        label: 'Topic or keyword',
        required: true,
        placeholder: 'e.g. climate change',
        help: 'Enter the subject you are interested in. We will search bill titles and content.',
      },
    ],
    build: (a) => `/bills?q=${encodeURIComponent(a.topic.trim())}`,
  },
];

export default function GuidedSearch() {
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [touched, setTouched] = useState(false);

  const startTemplate = (tpl) => {
    setTemplate(tpl);
    setActiveStep(0);
    setAnswers({});
    setTouched(false);
  };

  const resetToPicker = () => {
    setTemplate(null);
    setActiveStep(0);
    setAnswers({});
    setTouched(false);
  };

  const currentStep = template ? template.steps[activeStep] : null;
  const currentValue = currentStep ? (answers[currentStep.field] || '') : '';
  const stepInvalid =
    currentStep && currentStep.required && currentValue.trim() === '';

  const handleNext = () => {
    if (stepInvalid) {
      setTouched(true);
      return;
    }
    if (activeStep < template.steps.length - 1) {
      setActiveStep((s) => s + 1);
      setTouched(false);
    } else {
      // Final step — build the deep link and navigate into the existing
      // search page.
      navigate(template.build(answers));
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      resetToPicker();
    } else {
      setActiveStep((s) => s - 1);
      setTouched(false);
    }
  };

  const isLastStep = template && activeStep === template.steps.length - 1;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <SearchIcon color="primary" />
        <Typography variant="h4">
          Guided Search
        </Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Not sure how to phrase your search? Pick what you're looking for and we'll
        walk you through it step by step — no search syntax required.
      </Typography>

      {!template && (
        <Grid container spacing={2.5}>
          {TEMPLATES.map((tpl) => (
            <Grid item xs={12} sm={6} key={tpl.key}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    borderColor: tpl.color,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardActionArea
                  onClick={() => startTemplate(tpl)}
                  sx={{ height: '100%' }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 1,
                      }}
                    >
                      <Box sx={{ color: tpl.color, display: 'flex' }}>
                        {tpl.icon}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {tpl.label}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {tpl.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {template && (
        <Paper
          elevation={1}
          sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, maxWidth: 720 }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}
          >
            <Box sx={{ color: template.color, display: 'flex' }}>
              {template.icon}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {template.label}
            </Typography>
          </Box>

          {template.steps.length > 1 && (
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{ mb: 3 }}
            >
              {template.steps.map((s) => (
                <Step key={s.field}>
                  <StepLabel>{s.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {currentStep.label}
            {currentStep.required && (
              <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                *
              </Box>
            )}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            {currentStep.help}
          </Typography>

          <TextField
            fullWidth
            autoFocus
            value={currentValue}
            placeholder={currentStep.placeholder}
            onChange={(e) =>
              setAnswers((prev) => ({
                ...prev,
                [currentStep.field]: e.target.value,
              }))
            }
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleNext();
            }}
            error={touched && stepInvalid}
            helperText={
              touched && stepInvalid ? 'This field is required' : ' '
            }
          />

          {isLastStep && (
            <Alert
              severity="info"
              icon={<SearchIcon fontSize="inherit" />}
              sx={{ mt: 1, mb: 2, borderRadius: 2 }}
            >
              When you click <strong>Search</strong> we'll take you to the
              results, pre-filled — you can refine further from there.
            </Alert>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 3,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              color="inherit"
            >
              {activeStep === 0 ? 'Choose a different search' : 'Back'}
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={isLastStep ? <SearchIcon /> : null}
            >
              {isLastStep ? 'Search' : 'Next'}
            </Button>
          </Box>
        </Paper>
      )}

      {!template && (
        <Box sx={{ mt: 4 }}>
          <Chip
            label="Prefer to search yourself? Go to the homepage"
            variant="outlined"
            clickable
            onClick={() => navigate('/')}
          />
        </Box>
      )}
    </Box>
  );
}
